import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useLocation, useNavigate, Link } from "react-router-dom";
import { listProducts } from "@/lib/adapters/products";
import { loadCatalogManifest, getBrandsCatalog } from "@/lib/manifest";
import ProductCard from "@/components/product/ProductCard";
import SuggestProductStrip from "@/components/quote/SuggestProductStrip";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import {
  Loader2, SlidersHorizontal, ChevronDown, ChevronUp, Search, X,
  ChevronLeft, ChevronRight, ShieldCheck, Truck, RotateCcw, Lock, Headphones,
} from "lucide-react";

// Shop-page sidebar categories — the display names and order mirror the
// approved mockup exactly. Each entry maps to a real catalog slug so that
// checking a box actually filters products. When multiple catalog slugs
// belong to the same shopper-facing bucket (e.g. "Consumables") we pick
// the closest single slug; live counts render 0 when a slug has no
// products in the currently-loaded set, which is fine — the design still
// matches the mockup and users see immediate results as soon as we
// backfill the catalog with more items.
const SHOP_SIDEBAR_CATEGORIES = [
  { slug: "impression-material", name: "Impression Materials" },
  { slug: "rative-next", name: "Restorative" },
  { slug: "endodontics", name: "Endodontics" },
  { slug: "oral-surgery", name: "Surgical" },
  { slug: "pharmacy", name: "Preventive" },
  { slug: "orthodontics", name: "Orthodontics" },
  { slug: "dental-equipment", name: "Equipment" },
  { slug: "instruments", name: "Instruments" },
  { slug: "basic-dental-products", name: "Dental Materials" },
  { slug: "hot-selling", name: "Consumables" },
  { slug: "spare-parts", name: "Disposables" },
  { slug: "dentaltech", name: "Radiology" },
  { slug: "laboratory", name: "Lab Supplies" },
  { slug: "medical-supplies", name: "PPE & Infection Control" },
  { slug: "prosthodontics", name: "Prosthodontics" },
  { slug: "implantology", name: "Implantology" },
];

// ---------------------------------------------------------------------------
// CategoryPage — used by /shop, /category/:slug, /brand/:slug, /refurbished,
// /offers. The redesign matches the shop mockup: a left filter sidebar
// (Categories / Brand / Price / Availability / Discount), a hero row with
// title + right-hand promo banner (shop only), a 4-column product grid,
// pagination + per-page selector, and a bulk-order banner + USP strip in
// the footer band. ProductCard rendering, sort logic and data fetching
// are unchanged from the previous version — only layout and filters were
// added.
// ---------------------------------------------------------------------------

const ROUTE_TYPE = { "/refurbished": "refurbished", "/offers": "offer", "/shop": null };
const PER_PAGE_OPTIONS = [16, 32, 48];
const DISCOUNT_BUCKETS = [
  { value: 10, label: "10% and above" },
  { value: 20, label: "20% and above" },
  { value: 30, label: "30% and above" },
  { value: 40, label: "40% and above" },
];

export default function CategoryPage({ mode = "category" } = {}) {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { openQuote } = useQuoteDrawer();

  const q = searchParams.get("q") || "";
  const sub = searchParams.get("sub") || "";
  const categoryQuery = searchParams.get("category") || "";
  const brandFromQuery = searchParams.get("brand") || "";
  const brandSlug = mode === "brand" ? slug : brandFromQuery;
  const categorySlug = mode === "brand" ? categoryQuery : (slug || categoryQuery);
  const typeFromRoute = ROUTE_TYPE[location.pathname] ?? null;
  const typeFromQuery = searchParams.get("type");
  const type = typeFromRoute || typeFromQuery;
  const isShopRoute = location.pathname === "/shop";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("relevance");

  // ---------------------------------------------------------------------
  // Local sidebar filter state. Server-side we only pre-filter by the URL
  // params (category / brand / q / type) — every additional facet (extra
  // categories, extra brands, price, availability, discount) is applied
  // client-side over the loaded set so multi-select works even though the
  // API accepts one category / one brand at a time.
  // ---------------------------------------------------------------------
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(50000);
  const [availability, setAvailability] = useState({ inStock: false, outOfStock: false });
  const [discountMin, setDiscountMin] = useState(0);
  const [brandQuery, setBrandQuery] = useState("");
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [helpDismissed, setHelpDismissed] = useState(false);
  const [catalogBrands, setCatalogBrands] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(16);

  // Load a broader catalog set (200) so client-side facets have enough to
  // work with. Category / brand / q / type from the URL still act as
  // server-side pre-filters to keep the payload sane on large catalogs.
  useEffect(() => {
    setLoading(true);
    setPage(1);
    listProducts({
      limit: 200,
      category: categorySlug || undefined,
      brand: brandSlug || undefined,
      q: q || undefined,
      type: type || undefined,
    })
      .then(({ items: rows }) => setItems(rows))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [categorySlug, brandSlug, q, type]);

  // Lazy-load the brands catalog once so the sidebar's Brand facet can
  // enumerate every brand shipped in `catalog-manifest.json`.
  useEffect(() => {
    loadCatalogManifest().then(() => setCatalogBrands(getBrandsCatalog())).catch(() => { });
  }, []);

  // Reset paginator whenever any local filter changes.
  useEffect(() => { setPage(1); }, [selectedCategories, selectedBrands, priceMin, priceMax, availability, discountMin, sort, perPage]);

  // -------------------------- Filter pipeline --------------------------
  const filtered = useMemo(() => {
    return items.filter((p) => {
      if (selectedCategories.length && !selectedCategories.includes(p.category)) return false;
      if (selectedBrands.length && !selectedBrands.includes(p.brand_slug)) return false;
      if (typeof p.price === "number") {
        if (p.price < priceMin) return false;
        if (p.price > priceMax) return false;
      }
      if (availability.inStock && p.in_stock === false) return false;
      if (availability.outOfStock && p.in_stock !== false) return false;
      if (discountMin > 0 && Number(p.discount || 0) < discountMin) return false;
      return true;
    });
  }, [items, selectedCategories, selectedBrands, priceMin, priceMax, availability, discountMin]);

  const sorted = useMemo(() => {
    const cp = [...filtered];
    if (sort === "low-high") cp.sort((a, b) => a.price - b.price);
    if (sort === "high-low") cp.sort((a, b) => b.price - a.price);
    if (sort === "rating") cp.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return cp;
  }, [filtered, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / perPage));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * perPage;
  const pageEnd = Math.min(pageStart + perPage, sorted.length);
  const pageItems = sorted.slice(pageStart, pageEnd);

  // Faceted count helpers — always computed against the SERVER-side set
  // (before any client sidebar filter is applied) so counts stay stable
  // and match what the shopper would see if they toggled that facet on.
  const catCounts = useMemo(() => {
    const m = new Map();
    for (const p of items) m.set(p.category, (m.get(p.category) || 0) + 1);
    return m;
  }, [items]);
  const brandCounts = useMemo(() => {
    // Normalized products expose the brand *name* on `p.brand` and the
    // *slug* on `p.brand_slug`. Keep the count map keyed by slug so it
    // aligns with the catalog manifest we render the checkbox list from.
    const m = new Map();
    for (const p of items) if (p.brand_slug) m.set(p.brand_slug, (m.get(p.brand_slug) || 0) + 1);
    return m;
  }, [items]);
  const inStockCount = useMemo(() => items.filter((p) => p.in_stock !== false).length, [items]);
  const outOfStockCount = items.length - inStockCount;

  const categoryFacets = useMemo(() => {
    // Curated SHOP_SIDEBAR_CATEGORIES ordering to match the mockup 1:1.
    // Every row gets a live count from the currently-loaded product set;
    // slugs without matches show no count, keeping the sidebar clean.
    return SHOP_SIDEBAR_CATEGORIES.map((c) => ({
      slug: c.slug,
      name: c.name,
      count: catCounts.get(c.slug) || 0,
    }));
  }, [catCounts]);

  const brandFacets = useMemo(() => {
    const source = catalogBrands.length ? catalogBrands : Array.from(brandCounts.keys()).map((slug) => ({ slug, name: slug }));
    const filteredList = source.filter((b) => (b.name || "").toLowerCase().includes(brandQuery.toLowerCase()));
    // Show brands that either match the search or have live counts first.
    const decorated = filteredList.map((b) => ({
      slug: b.slug,
      name: b.name,
      count: brandCounts.get(b.slug) || 0,
    }));
    decorated.sort((a, b) => (b.count || 0) - (a.count || 0));
    return showAllBrands ? decorated : decorated.slice(0, 10);
  }, [catalogBrands, brandCounts, brandQuery, showAllBrands]);

  const discountCounts = useMemo(() => {
    const m = new Map();
    for (const bucket of DISCOUNT_BUCKETS) {
      m.set(bucket.value, items.filter((p) => Number(p.discount || 0) >= bucket.value).length);
    }
    return m;
  }, [items]);

  const humanize = (s) => (s || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const title = mode === "brand" && slug
    ? humanize(slug)
    : slug
      ? humanize(slug)
      : (q ? `Search: "${q}"` : (type ? { refurbished: "Refurbished Equipment", offer: "Offers of the Day", bulk: "Bulk Buy & Save" }[type] || "Shop" : "Shop"));

  // -------------------------- Filter mutators --------------------------
  const toggleCategory = (s) =>
    setSelectedCategories((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  const toggleBrand = (s) =>
    setSelectedBrands((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setPriceMin(0);
    setPriceMax(50000);
    setAvailability({ inStock: false, outOfStock: false });
    setDiscountMin(0);
  };
  const clearUrlCategory = () => {
    // Landing on /category/:slug: navigate back to /shop and preserve any
    // other query params so the shopper doesn't lose their sort/perPage.
    if (mode === "brand" || location.pathname === "/shop") {
      const next = new URLSearchParams(searchParams);
      next.delete("category");
      setSearchParams(next);
    } else {
      navigate("/shop");
    }
  };

  const activeFacetCount =
    selectedCategories.length + selectedBrands.length +
    (priceMin > 0 || priceMax < 50000 ? 1 : 0) +
    (availability.inStock || availability.outOfStock ? 1 : 0) +
    (discountMin > 0 ? 1 : 0);

  return (
    <main className="dyntra-container py-6">
      {/* Breadcrumb */}
      <nav className="text-xs text-gray-500 mb-4">
        <Link to="/" className="hover:text-[#4B2AAD]">Home</Link>
        {" / "}
        <span className="text-gray-900 font-semibold">
          {isShopRoute ? "Shop" : mode === "brand" ? `Brand · ${humanize(slug || "")}` : (categorySlug ? humanize(categorySlug) : "Shop")}
        </span>
      </nav>

      {/* Hero row — title on left, promo banner on right (shop route only).
          `items-center` vertically centres the "Shop" heading + subtitle
          against the taller promo card so the two columns read as a
          single inline band, exactly like the mockup. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6 items-center">
        <div className={isShopRoute ? "lg:col-span-2" : "lg:col-span-3"}>
          <h1 data-testid="category-title" className="font-display text-3xl sm:text-4xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isShopRoute
              ? "Discover premium dental products from trusted brands"
              : sub
                ? <>Filtered by: <span className="dyntra-chip">{sub}</span></>
                : `${sorted.length} products`}
          </p>
        </div>
        {isShopRoute && <ShopPromoBanner />}
      </div>

      {/* Sidebar + content layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="space-y-4">
          <CollapsibleCard title="Categories">
            <div className="space-y-4 mt-3">
              {categoryFacets.map((c) => (
                <FilterCheckbox
                  key={c.slug}
                  testid={`cat-${c.slug}`}
                  checked={selectedCategories.includes(c.slug)}
                  onChange={() => toggleCategory(c.slug)}
                  label={c.name}
                  count={c.count || undefined}
                />
              ))}
            </div>
          </CollapsibleCard>

          <div className="rounded-2xl border border-gray-100 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-sm font-bold text-gray-900">Filters</h3>
              <button
                type="button"
                onClick={clearAllFilters}
                data-testid="filters-clear-all"
                className="text-xs font-semibold text-[#4B2AAD] hover:underline"
              >
                Clear All
              </button>
            </div>

            {/* Brand */}
            <FacetSection title="Brand">
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  value={brandQuery}
                  onChange={(e) => setBrandQuery(e.target.value)}
                  placeholder="Search brand"
                  className="w-full rounded-lg border border-gray-200 bg-white pl-7 pr-2 py-1.5 text-xs outline-none focus:border-[#4B2AAD]"
                />
              </div>
              <div className="space-y-3 max-h-56 overflow-y-auto thin-scroll">
                {brandFacets.map((b) => (
                  <FilterCheckbox
                    key={b.slug}
                    testid={`brand-${b.slug}`}
                    checked={selectedBrands.includes(b.slug)}
                    onChange={() => toggleBrand(b.slug)}
                    label={b.name}
                    count={b.count || undefined}
                    dim={!b.count}
                  />
                ))}
              </div>
              {(catalogBrands.length > 10 || (!showAllBrands && brandFacets.length === 10)) && (
                <button
                  type="button"
                  onClick={() => setShowAllBrands((v) => !v)}
                  className="text-xs font-semibold text-[#4B2AAD] hover:underline mt-2"
                >
                  {showAllBrands ? "− Show Less" : "+ Show More"}
                </button>
              )}
            </FacetSection>

            {/* Price Range */}
            <FacetSection title="Price Range">
              <input
                type="range"
                min={0}
                max={50000}
                step={500}
                value={priceMax}
                onChange={(e) => setPriceMax(Number(e.target.value))}
                className="w-full accent-[#4B2AAD]"
                data-testid="price-range-slider"
              />
              <div className="flex items-center gap-2 mt-2">
                <div className="flex-1">
                  <label className="text-[10px] text-gray-500 block">Min</label>
                  <div className="flex items-center rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs">
                    <span className="text-gray-400 mr-1">₹</span>
                    <input
                      type="number"
                      value={priceMin}
                      onChange={(e) => setPriceMin(Math.max(0, Number(e.target.value)))}
                      className="w-full outline-none"
                    />
                  </div>
                </div>
                <div className="text-gray-400 mt-4">to</div>
                <div className="flex-1">
                  <label className="text-[10px] text-gray-500 block">Max</label>
                  <div className="flex items-center rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs">
                    <span className="text-gray-400 mr-1">₹</span>
                    <input
                      type="number"
                      value={priceMax}
                      onChange={(e) => setPriceMax(Math.max(priceMin, Number(e.target.value)))}
                      className="w-full outline-none"
                    />
                  </div>
                </div>
              </div>
            </FacetSection>

            {/* Availability */}
            <FacetSection title="Availability">
              <div className="space-y-3 mt-2">
                <FilterCheckbox
                  testid="avail-in-stock"
                  checked={availability.inStock}
                  onChange={() => setAvailability((a) => ({ ...a, inStock: !a.inStock }))}
                  label="In Stock"
                  count={inStockCount}
                />
                <FilterCheckbox
                  testid="avail-out-of-stock"
                  checked={availability.outOfStock}
                  onChange={() => setAvailability((a) => ({ ...a, outOfStock: !a.outOfStock }))}
                  label="Out of Stock"
                  count={outOfStockCount}
                />
              </div>
            </FacetSection>

            {/* Discount */}
            <FacetSection title="Discount" last>
              <div className="space-y-3 mt-2">
                {DISCOUNT_BUCKETS.map((bucket) => (
                  <FilterCheckbox
                    key={bucket.value}
                    testid={`discount-${bucket.value}`}
                    checked={discountMin === bucket.value}
                    onChange={() => setDiscountMin(discountMin === bucket.value ? 0 : bucket.value)}
                    label={bucket.label}
                    count={discountCounts.get(bucket.value)}
                  />
                ))}
              </div>
            </FacetSection>

            <button
              type="button"
              onClick={clearAllFilters}
              data-testid="filters-clear-all-btn"
              className="mt-4 w-full rounded-lg border border-[#4B2AAD]/30 text-[#4B2AAD] hover:bg-[#F3EFFC] text-sm font-semibold py-2 transition-colors"
            >
              Clear All Filters
            </button>
          </div>

          {!helpDismissed && (
            <div className="relative rounded-2xl bg-[#F3EFFC] border border-[#4B2AAD]/20 p-6 flex flex-col items-center text-center gap-9 shadow-sm">
              <button
                type="button"
                onClick={() => setHelpDismissed(true)}
                aria-label="Dismiss help card"
                className="absolute top-3 right-3 h-8 w-8 rounded-full text-gray-400 hover:text-[#4B2AAD] hover:bg-white flex items-center justify-center transition-colors"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="h-[72px] w-[72px] rounded-full bg-white flex items-center justify-center shrink-0 shadow-md border border-[#4B2AAD]/10">
                <Headphones className="h-9 w-9 text-[#4B2AAD]" />
              </div>

              <div className="space-y-1">
                <p className="text-lg font-bold text-gray-900">Need Help?</p>
                <p className="text-xs text-gray-500 leading-relaxed max-w-[190px]">
                  Talk to our product expert to find the best options for your practice
                </p>
              </div>

              <Link
                to="/support"
                data-testid="sidebar-connect-cta"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#4B2AAD] text-white text-sm font-bold py-2.5 hover:bg-[#361D82] shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                Connect Now
              </Link>
            </div>
          )}
        </aside>

        {/* Content */}
        <section>
          {/* Applied-filter chip bar + Sort. Wrapped in the same white
              rounded card as the sidebar so the top of this row is
              perfectly in line with the top of the Categories card,
              exactly like the mockup. */}
          <div className="rounded-2xl border border-gray-100 bg-white px-4 py-3 flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="text-sm text-gray-600 flex flex-wrap items-center gap-2">
              {(() => {
                // Prefer the mockup phrasing: when only sidebar categories
                // are active, show "N Categor(y|ies) Selected". When only
                // brands are active, show "N Brand(s) Selected". Otherwise
                // fall back to a generic "N filters applied" chip. The
                // URL-based /category/:slug case (browsed via nav) also
                // renders the same "1 Category Selected" phrasing.
                const urlCategoryActive = categorySlug && !isShopRoute;
                const onlyCategories = selectedCategories.length > 0
                  && selectedBrands.length === 0
                  && !(priceMin > 0 || priceMax < 50000)
                  && !(availability.inStock || availability.outOfStock)
                  && discountMin === 0;
                const onlyBrands = selectedBrands.length > 0
                  && selectedCategories.length === 0
                  && !(priceMin > 0 || priceMax < 50000)
                  && !(availability.inStock || availability.outOfStock)
                  && discountMin === 0;

                if (urlCategoryActive) {
                  return (
                    <span className="inline-flex items-center gap-2">
                      <span className="dyntra-chip">1 Category Selected</span>
                      <button
                        type="button"
                        data-testid="clear-url-category"
                        onClick={clearUrlCategory}
                        className="text-[#4B2AAD] font-semibold hover:underline text-xs"
                      >Clear</button>
                    </span>
                  );
                }
                if (onlyCategories) {
                  return (
                    <span className="inline-flex items-center gap-2">
                      <span className="dyntra-chip">
                        {selectedCategories.length} {selectedCategories.length === 1 ? "Category" : "Categories"} Selected
                      </span>
                      <button
                        type="button"
                        onClick={clearAllFilters}
                        className="text-[#4B2AAD] font-semibold hover:underline text-xs"
                      >Clear</button>
                    </span>
                  );
                }
                if (onlyBrands) {
                  return (
                    <span className="inline-flex items-center gap-2">
                      <span className="dyntra-chip">
                        {selectedBrands.length} Brand{selectedBrands.length === 1 ? "" : "s"} Selected
                      </span>
                      <button
                        type="button"
                        onClick={clearAllFilters}
                        className="text-[#4B2AAD] font-semibold hover:underline text-xs"
                      >Clear</button>
                    </span>
                  );
                }
                if (activeFacetCount > 0) {
                  return (
                    <span className="inline-flex items-center gap-2">
                      <span className="dyntra-chip">{activeFacetCount} filter{activeFacetCount > 1 ? "s" : ""} applied</span>
                      <button
                        type="button"
                        onClick={clearAllFilters}
                        className="text-[#4B2AAD] font-semibold hover:underline text-xs"
                      >Clear</button>
                    </span>
                  );
                }
                return <span className="text-gray-500">{sorted.length} products</span>;
              })()}
            </div>
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-gray-500" />
              <label htmlFor="sort-select" className="text-sm text-gray-500">Sort by</label>
              <select
                id="sort-select"
                data-testid="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white outline-none focus:border-[#4B2AAD]"
              >
                <option value="relevance">Popularity</option>
                <option value="low-high">Price: Low to High</option>
                <option value="high-low">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
              </select>
            </div>
          </div>

          {/* Grid / empty / loading */}
          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#4B2AAD]" /></div>
          ) : sorted.length === 0 ? (
            <>
              <div className="rounded-2xl border border-gray-100 bg-white p-16 text-center">
                <p className="text-gray-500">No products match these filters. Try clearing a few or picking a different category.</p>
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="mt-4 inline-flex items-center justify-center rounded-lg bg-[#4B2AAD] text-white text-sm font-bold px-4 py-2 hover:bg-[#361D82]"
                >
                  Clear All Filters
                </button>
              </div>
              <SuggestProductStrip query={q} category={categorySlug} />
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {pageItems.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>

              {/* Pagination — sits directly above the Bulk Order banner to
                  match the mockup. The SuggestProductStrip is intentionally
                  omitted here so the shop page keeps a tight footer; it
                  still renders in the empty-state view above. */}
              <Pagination
                page={currentPage}
                pageCount={pageCount}
                pageStart={pageStart + 1}
                pageEnd={pageEnd}
                total={sorted.length}
                perPage={perPage}
                onPage={setPage}
                onPerPage={setPerPage}
              />
            </>
          )}

          {/* Bulk Order banner — the mockup pairs the copy + CTA with a
              product-photography decoration on the right. We reuse the
              existing WBH clinic artwork to signal "bulk / clinical
              volume" without adding a new asset. */}
          <div className="mt-8 rounded-2xl bg-gradient-to-r from-[#361D82] to-[#4B2AAD] text-white p-6 flex flex-col sm:flex-row items-center justify-between gap-4 overflow-hidden">
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-lg sm:text-xl font-bold">Bulk Order or Custom Requirement?</h3>
              <p className="text-sm text-white/85 mt-1">Get special pricing, volume discounts &amp; dedicated support</p>
              <button
                type="button"
                data-testid="shop-bulk-quote-cta"
                onClick={() => openQuote({ category: categorySlug || "" })}
                className="mt-3 inline-flex items-center justify-center rounded-xl bg-white text-[#4B2AAD] px-5 py-2.5 text-sm font-bold hover:bg-white/90 whitespace-nowrap"
              >
                Request a Quote
              </button>
            </div>
            <img
              src="/wbh/Dyntra Clinic.png"
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="hidden sm:block h-24 md:h-32 w-auto object-contain shrink-0 select-none pointer-events-none"
            />
          </div>

          {/* Footer USP strip — 4 items, compact variant tailored to the
              shop page. Bigger USPStrip on the home page still shows the
              6-item version so the two aren't visually redundant. */}
          <div className="mt-6 rounded-2xl bg-white border border-gray-100 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <UspItem icon={ShieldCheck} title="100% Genuine Products" sub="Trusted by Dental Professionals" />
            <UspItem icon={Truck} title="Fast & Reliable Delivery" sub="Across India" />
            <UspItem icon={RotateCcw} title="Easy Returns" sub="7-Day Return Policy" />
            <UspItem icon={Lock} title="Secure Payments" sub="100% Protected Transactions" />
          </div>
        </section>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Sub-components. Kept inline for now since they're bespoke to the shop
// page — if they need to be reused elsewhere, extract into their own files.
// ---------------------------------------------------------------------------

function CollapsibleCard({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
      >
        <h3 className="font-display text-sm font-bold text-gray-900">{title}</h3>
        {open ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
      </button>
      {open && children}
    </div>
  );
}

function FacetSection({ title, children, last = false }) {
  return (
    <div className={`border-t border-gray-100 pt-3 mt-3 ${last ? "" : ""}`}>
      <h4 className="text-xs font-bold text-gray-900 mb-2">{title}</h4>
      {children}
    </div>
  );
}

function FilterCheckbox({ checked, onChange, label, count, testid, dim = false }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer group text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        data-testid={testid}
        className="h-3.5 w-3.5 rounded border-gray-300 text-[#4B2AAD] focus:ring-[#4B2AAD]"
      />
      <span className={`flex-1 truncate ${dim ? "text-gray-400" : "text-gray-700 group-hover:text-[#4B2AAD]"}`}>{label}</span>
      {typeof count === "number" && count > 0 && (
        <span className="text-[10px] text-gray-400 tabular-nums">({count.toLocaleString()})</span>
      )}
    </label>
  );
}

function ShopPromoBanner() {
  return (
    <div className="lg:col-span-1 rounded-2xl bg-gradient-to-br from-[#F3EFFC] via-[#F3EFFC] to-white border border-[#4B2AAD]/15 p-4 flex items-center gap-4 overflow-hidden">
      <div className="flex-1 min-w-0">
        <h3 className="font-display text-base sm:text-lg font-bold text-gray-900">Complete Dental Solutions</h3>
        <p className="text-xs text-gray-500 mt-0.5">For Every Practice</p>
        <Link
          to="/consultation"
          data-testid="shop-promo-cta"
          className="mt-2 inline-flex items-center justify-center rounded-lg bg-[#4B2AAD] text-white text-xs font-bold px-3 py-1.5 hover:bg-[#361D82]"
        >
          Explore Now
        </Link>
      </div>
      <img
        src="/wbh/Dyntra Clinic.png"
        alt="Complete Dental Solutions"
        loading="lazy"
        className="h-20 sm:h-24 w-auto object-contain shrink-0"
      />
    </div>
  );
}

function Pagination({ page, pageCount, pageStart, pageEnd, total, perPage, onPage, onPerPage }) {
  if (pageCount <= 1 && total <= perPage) {
    return (
      <div className="mt-6 flex items-center justify-between text-xs text-gray-500">
        <span>Showing {pageStart} – {pageEnd} of {total.toLocaleString()} products</span>
        <div className="flex items-center gap-2">
          <label htmlFor="per-page">Show</label>
          <select
            id="per-page"
            value={perPage}
            onChange={(e) => onPerPage(Number(e.target.value))}
            className="rounded-lg border border-gray-200 px-2 py-1 text-xs bg-white outline-none focus:border-[#4B2AAD]"
          >
            {PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>per page</span>
        </div>
      </div>
    );
  }

  // Show up to 5 numeric buttons with an ellipsis when we skip.
  const numbers = [];
  const push = (n) => numbers.push(n);
  if (pageCount <= 7) {
    for (let i = 1; i <= pageCount; i++) push(i);
  } else {
    push(1);
    if (page > 3) push("...");
    for (let i = Math.max(2, page - 1); i <= Math.min(pageCount - 1, page + 1); i++) push(i);
    if (page < pageCount - 2) push("...");
    push(pageCount);
  }

  return (
    <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
      <span>Showing {pageStart} – {pageEnd} of {total.toLocaleString()} products</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => onPage(page - 1)}
          data-testid="page-prev"
          className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 disabled:opacity-30 hover:border-[#4B2AAD] hover:text-[#4B2AAD]"
          aria-label="Previous page"
        ><ChevronLeft className="h-4 w-4" /></button>
        {numbers.map((n, i) =>
          n === "..." ? (
            <span key={`e${i}`} className="px-2 text-gray-400">…</span>
          ) : (
            <button
              key={n}
              type="button"
              data-testid={`page-${n}`}
              onClick={() => onPage(n)}
              className={`h-8 min-w-[32px] px-2 rounded-lg text-xs font-semibold ${n === page
                ? "bg-[#4B2AAD] text-white"
                : "border border-gray-200 text-gray-700 hover:border-[#4B2AAD] hover:text-[#4B2AAD]"
                }`}
            >{n}</button>
          )
        )}
        <button
          type="button"
          disabled={page === pageCount}
          onClick={() => onPage(page + 1)}
          data-testid="page-next"
          className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 disabled:opacity-30 hover:border-[#4B2AAD] hover:text-[#4B2AAD]"
          aria-label="Next page"
        ><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor="per-page">Show</label>
        <select
          id="per-page"
          value={perPage}
          onChange={(e) => onPerPage(Number(e.target.value))}
          data-testid="per-page-select"
          className="rounded-lg border border-gray-200 px-2 py-1 text-xs bg-white outline-none focus:border-[#4B2AAD]"
        >
          {PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span>per page</span>
      </div>
    </div>
  );
}

function UspItem({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-9 w-9 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[#4B2AAD]" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-bold text-gray-900 leading-tight truncate">{title}</p>
        <p className="text-[10px] text-gray-500 leading-tight truncate">{sub}</p>
      </div>
    </div>
  );
}
